import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

const TRACK = {
  title: 'Caddelerde Rüzgar',
  artist: 'UFO361',
  audioSrc: '/sounds/caddelerde-ruzgar.mp3',
  coverSrc: '/images/caddelerderuzgar.png'
};

class Component extends React.PureComponent {
  static displayName = 'MusicPlayer';

  static propTypes = {
    classes: PropTypes.object.isRequired,
    className: PropTypes.string
  };

  constructor (props) {
    super(props);

    this.state = {
      isMuted: true,
      progress: 0
    };

    this.audio = null;
    this.gainNode = null;
  }

  componentDidMount () {
    if (typeof window !== 'undefined') {
      this.setupAudio();
    }
  }

  componentWillUnmount () {
    if (this.audio) {
      this.audio.removeEventListener('timeupdate', this.onTimeUpdate);
      this.audio.pause();
      this.audio = null;
    }
  }

  onTimeUpdate = () => {
    if (this.audio && this.audio.duration) {
      const progress = (this.audio.currentTime / this.audio.duration) * 100;
      this.setState({ progress: Math.min(100, Math.max(0, progress)) });
    }
  };

  setupAudio () {
    const track = TRACK;

    this.audio = new Audio();
    this.audio.preload = 'none';
    this.audio.src = track.audioSrc;
    this.audio.loop = true;
    this.audio.volume = 1.0;
    this.audio.crossOrigin = 'anonymous';
    this.audio.addEventListener('timeupdate', this.onTimeUpdate);
    window.__bgAudioElement = this.audio;

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        if (!window.__siteAudioCtx) {
          window.__siteAudioCtx = new AudioContext();
        }
        const ctx = window.__siteAudioCtx;

        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.78;

        const gainNode = ctx.createGain();
        gainNode.gain.setValueAtTime(0, ctx.currentTime);

        const source = ctx.createMediaElementSource(this.audio);
        source.connect(analyser);
        analyser.connect(gainNode);
        gainNode.connect(ctx.destination);

        this.gainNode = gainNode;
        window.__bgAudioAnalyser = analyser;
        window.__bgAudioIsMuted = true;
      }
    } catch (e) {
      this.audio.muted = true;
      window.__bgAudioIsMuted = true;
    }
  }

  toggleMute = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.audio) return;

    const nextMuted = !this.state.isMuted;

    if (!nextMuted) {
      if (window.__siteAudioCtx && window.__siteAudioCtx.state === 'suspended') {
        window.__siteAudioCtx.resume().catch(() => {});
      }
      this.audio.play().catch(() => {});

      if (this.gainNode && window.__siteAudioCtx) {
        const now = window.__siteAudioCtx.currentTime;
        this.gainNode.gain.cancelScheduledValues(now);
        this.gainNode.gain.setValueAtTime(1.25, now);
        this.audio.muted = false;
      } else {
        this.audio.volume = 1.0;
        this.audio.muted = false;
      }
      window.__bgAudioIsMuted = false;
    } else {
      if (this.gainNode && window.__siteAudioCtx) {
        const now = window.__siteAudioCtx.currentTime;
        this.gainNode.gain.cancelScheduledValues(now);
        this.gainNode.gain.setValueAtTime(0, now);
      }
      this.audio.pause();
      window.__bgAudioIsMuted = true;
    }

    this.setState({ isMuted: nextMuted });
  }

  render () {
    const { classes, className } = this.props;
    const { isMuted, progress } = this.state;
    const track = TRACK;

    return (
      <div
        className={cx(classes.root, className)}
        onClick={this.toggleMute}
        role='button'
        tabIndex={0}
        aria-label={isMuted ? `Sesi Aç (${track.artist} - ${track.title})` : 'Sesi Kapat'}
      >
        <div className={classes.recordWrapper}>
          <div className={cx(classes.record, !isMuted && classes.recordUnmuted)}>
            <div
              className={classes.recordCenter}
              style={{ backgroundImage: `url(${track.coverSrc})` }}
            />
          </div>
        </div>

        <div className={classes.info}>
          <span className={classes.title}>{track.title}</span>
          <span className={classes.artist}>{track.artist}</span>
        </div>

        <button
          type='button'
          className={cx(classes.button, !isMuted && classes.buttonActive)}
          onClick={this.toggleMute}
          aria-label={isMuted ? 'Sesi Aç' : 'Sesi Kapat'}
        >
          {isMuted ? (
            <svg
              className={classes.buttonIcon}
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path d='M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z' />
            </svg>
          ) : (
            <svg
              className={classes.buttonIcon}
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path d='M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z' />
            </svg>
          )}
        </button>

        <div className={classes.progressTrack}>
          <div
            className={classes.progressFill}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  }
}

export { Component };
